import React, { useState ,useEffect ,useMemo} from "react";

import { Box, Typography, Paper, useTheme, Dialog, DialogContent } from "@mui/material";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import ProgressiveAnalysisTracker from "../../mech/casting/ProgressiveAnalysisTracker";
import FormulationTable from "../../../shared/modules/FormulationTable";
import ResultsSection from "../../../shared/sections/ResultsSection";
import { GlassbottlesQuestions } from "./questions";
import { GlassbottlesPlayBook } from "./playbook";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { apiFetch } from "../../../api/apiClient";

const DEFAULT_BREAKDOWN_ORDER = ["material", "process", "consumables", "ohp"];
// const normalizeKey = (key = "") => key.toLowerCase().replace(/[\s_-]+/g, "");
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics", "packaging"]);
// const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "logistics","total"].map(normalizeKey));
// const ALLOWED_BREAKDOWN_KEYS = new Set(DEFAULT_BREAKDOWN_ORDER.map(normalizeKey));
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  consumables: "Consumables",
  // packaging: "Packaging",
  ohp: "Overhead & Profits",
  logistics: "Logistics",
};
 
const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
 
// OHP Rate = (OHP / Total) * 100 - matches the percentage shown in cost category table
const calculateOhpRateFromCostData = (costData) => {
  if (!costData || !costData.breakdown) {
    return 0;
  }
  const ohp = Number(costData.breakdown.ohp || 0);
  const total = Number(
    costData.breakdown.total_should_cost || 
    costData.breakdown.total || 
    0
  );
  
  if (total > 0) {
    return Number(((ohp / total) * 100).toFixed(2));
  }
  
  // Fallback: calculate total from breakdown if not available
  const material = Number(costData.breakdown.material || 0);
  const process = Number(costData.breakdown.process || 0);
  const calculatedTotal = material + process + ohp;
  
  if (calculatedTotal > 0) {
    return Number(((ohp / calculatedTotal) * 100).toFixed(2));
  }
  return 0;
};

const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    // const normKey = normalizeKey(key);
    // if (SKIP_BREAKDOWN_KEYS.has(normKey)) return false;
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    // if (ALLOWED_BREAKDOWN_KEYS.size > 0 && !ALLOWED_BREAKDOWN_KEYS.has(normKey)) return false;
    return Number.isFinite(Number(value));
  });
 
  entries.sort((a, b) => {
    // const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(a[0]));
    // const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(b[0]));
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });
 
  return entries.map(([key, value]) => {
    // const normKey = normalizeKey(key);
    // const label = BREAKDOWN_LABELS[key] || BREAKDOWN_LABELS[normKey] || toLabel(key);
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};

// Handling assumption section locally and rendering the component here
function renderAssumptions({
  assumptions = [],
  productData,
  formulaResult,
  perlColors,
  perlTypography,
}) {

  return (
    <Box>
      {assumptions && assumptions.length > 0 && (
        <Paper
          sx={{
            p: 3,
            bgcolor: perlColors.surface,
            border: `1px solid ${perlColors.border}`,
            borderRadius: 2,
            mt: 3,
          }}
        >
          {(() => {
            const bottomSectionKeywords = [
              "Material price:",
              "Material Price:",
              "Labour cost:",
              "Labour Rate:",
              "Machine rate:",
              "Machine Rate:",
              "OHP rate:",
              "OHP:",
            ];

            // Case-insensitive matching for keywords
            const topAssumptions = assumptions.filter(
              (assumption) => {
                // Ensure assumption is a string
                const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                return !bottomSectionKeywords.some((keyword) =>
                  assumptionStr.toLowerCase().startsWith(keyword.toLowerCase())
                );
              }
            );
            const bottomAssumptions = assumptions.filter((assumption) => {
              // Ensure assumption is a string
              const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
              return bottomSectionKeywords.some((keyword) =>
                assumptionStr.toLowerCase().startsWith(keyword.toLowerCase())
              );
            });

            // Build product information from productData if available
            const buildProductInfo = () => {
              if (!productData) {
                // Fallback to topAssumptions if productData not available
                return topAssumptions.map((assumption) => assumption);
              }

              const productInfo = [];

              // Product Name
              if (productData.productName) {
                productInfo.push(`Product Name: ${productData.productName}`);
              }

              // Material Selection
              if (productData.materialSelection) {
                productInfo.push(`Material: ${productData.materialSelection}`);
              }

              // Batch Size
              if (productData.batchSize) {
                productInfo.push(`Batch Size: ${productData.batchSize}`);
              }

              // Annual Volume
              if (productData.annualVolume) {
                productInfo.push(`Annual Volume: ${productData.annualVolume}`);
              }

              // Part Weight and Dimensions
              const dims = productData.partWeightDimensions;
              if (dims) {
                const dimParts = [];
                if (dims.finishWeight) dimParts.push(`Weight: ${dims.finishWeight}g`);
                if (dims.partThickness) dimParts.push(`Thickness: ${dims.partThickness}mm`);
                if (dimParts.length > 0) {
                  productInfo.push(`Part Dimensions: ${dimParts.join(", ")}`);
                }
              }

              // Manufacturing Country
              if (productData.manufacturingCountry) {
                productInfo.push(
                  `Manufacturing Country: ${productData.manufacturingCountry}`
                );
              }

              // Don't add remainingAssumptions - only show actual product data
              return productInfo;
            };

            const productInfoItems = buildProductInfo();

            return (
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      fontFamily: perlTypography.family,
                      letterSpacing: "0.3px",
                      color: perlColors.textPrimary,
                    }}
                  >
                    Product Information
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {productInfoItems.map((item, idx) => {
                      // Ensure item is a string (handle objects, arrays, etc.)
                      const itemStr = typeof item === 'string' 
                        ? item 
                        : (typeof item === 'object' && item !== null 
                            ? (item.value || item.name || item.reason || JSON.stringify(item))
                            : String(item || ''));
                      return (
                        <Typography
                          key={idx}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            lineHeight: "20px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {itemStr}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>

                {bottomAssumptions.length > 0 && (
                  <Box
                    sx={{
                      width: "1px",
                      bgcolor: perlColors.border || "rgba(255, 255, 255, 0.12)",
                      alignSelf: "stretch",
                    }}
                  />
                )}

                {bottomAssumptions.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "18px",
                        fontFamily: perlTypography.family,
                        letterSpacing: "0.3px",
                        color: perlColors.textPrimary,
                      }}
                    >
                      Assumptions
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {bottomAssumptions.map((assumption, idx) => {
                        // Ensure assumption is a string
                        const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                        return (
                          <Typography
                            key={idx}
                            component="li"
                            variant="body2"
                            sx={{
                              mb: 1,
                              fontSize: "14px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textSecondary,
                              lineHeight: "20px",
                              letterSpacing: "0.3px",
                            }}
                          >
                            {assumptionStr}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Paper>
      )}
    </Box>
  );
}

export default function GlassbottlesPage() {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
    textSecondary: "#A0A0A0",
    surface: "#1E1F20",
    border: "rgba(255, 255, 255, 0.12)",
  };
  const perlTypography = theme.custom?.perlTypography || {};
  const perlSpacing = theme.custom?.perlSpacing || {};

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [keyInsightsData, setKeyInsightsData] = useState(null);

  // Progressive analysis tracker state
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));
    
    // Initialize playBookData from costData when starting playbook section
    if (activeSection === "playbook" && costData) {
      // Calculate OHP rate from costData (matching the percentage in cost category table)
      const calculatedOhpRate = calculateOhpRateFromCostData(costData);
      
      // Extract cycle time from costData (from backend response)
      let cycleTimeSeconds = 0;
      if (costData?.cycleTime_sec !== undefined && costData.cycleTime_sec !== null) {
        cycleTimeSeconds = parseFloat(costData.cycleTime_sec) || 0;
      } else if (costData?._originalResult?.cycleTime_sec !== undefined) {
        cycleTimeSeconds = parseFloat(costData._originalResult.cycleTime_sec) || 0;
      } else if (costData?._originalResult?.AssumptionsJson?.benchmarked_assumptions?.process_benchmarks?.primary_forming?.cycle_time_sec !== undefined) {
        cycleTimeSeconds = parseFloat(costData._originalResult.AssumptionsJson.benchmarked_assumptions.process_benchmarks.primary_forming.cycle_time_sec) || 0;
      }
      
      // Initialize playBookData with OHP rate and cycle time if available
      setPlayBookData((prevPlayBookData) => {
        const currentCostParams = prevPlayBookData?.costParams || {};
        const updates = { ...currentCostParams };
        
        // Only update ohpRate if not already set (preserve user input)
        if (calculatedOhpRate > 0 && (currentCostParams.ohpRate === undefined || currentCostParams.ohpRate === 0 || currentCostParams.ohpRate === null)) {
          updates.ohpRate = calculatedOhpRate;
        }
        
        // Only update cycleTime if not already set (preserve user input)
        if (cycleTimeSeconds > 0 && (currentCostParams.cycleTime === undefined || currentCostParams.cycleTime === 0 || currentCostParams.cycleTime === null)) {
          updates.cycleTime = cycleTimeSeconds;
        }
        
        // Only update if there are changes
        if (Object.keys(updates).length > Object.keys(currentCostParams).length || 
            updates.ohpRate !== currentCostParams.ohpRate || 
            updates.cycleTime !== currentCostParams.cycleTime) {
          return {
            ...prevPlayBookData,
            costParams: updates,
          };
        }
        return prevPlayBookData;
      });
    }
  };

  const handleProductComplete = (data) => {
    setProductData(data);
  };

  // Initialize playBookData OHP rate and cycle time from costData when costData is available
  useEffect(() => {
    if (costData && sectionStarted.playbook) {
      // Calculate OHP rate from costData (matching the percentage in cost category table)
      const calculatedOhpRate = calculateOhpRateFromCostData(costData);
      
      // Extract cycle time from costData (from backend response)
      let cycleTimeSeconds = 0;
      if (costData?.cycleTime_sec !== undefined && costData.cycleTime_sec !== null) {
        cycleTimeSeconds = parseFloat(costData.cycleTime_sec) || 0;
      } else if (costData?._originalResult?.cycleTime_sec !== undefined) {
        cycleTimeSeconds = parseFloat(costData._originalResult.cycleTime_sec) || 0;
      } else if (costData?._originalResult?.AssumptionsJson?.benchmarked_assumptions?.process_benchmarks?.primary_forming?.cycle_time_sec !== undefined) {
        cycleTimeSeconds = parseFloat(costData._originalResult.AssumptionsJson.benchmarked_assumptions.process_benchmarks.primary_forming.cycle_time_sec) || 0;
      }
      
      // Initialize playBookData with OHP rate and cycle time if available
      setPlayBookData((prevPlayBookData) => {
        const currentCostParams = prevPlayBookData?.costParams || {};
        const updates = { ...currentCostParams };
        
        // Only update ohpRate if not already set (preserve user input)
        if (calculatedOhpRate > 0 && (currentCostParams.ohpRate === undefined || currentCostParams.ohpRate === 0 || currentCostParams.ohpRate === null)) {
          updates.ohpRate = calculatedOhpRate;
        }
        
        // Only update cycleTime if not already set (preserve user input)
        if (cycleTimeSeconds > 0 && (currentCostParams.cycleTime === undefined || currentCostParams.cycleTime === 0 || currentCostParams.cycleTime === null)) {
          updates.cycleTime = cycleTimeSeconds;
        }
        
        // Only update if there are changes
        if (Object.keys(updates).length > Object.keys(currentCostParams).length || 
            updates.ohpRate !== currentCostParams.ohpRate || 
            updates.cycleTime !== currentCostParams.cycleTime) {
          return {
            ...prevPlayBookData,
            costParams: updates,
          };
        }
        return prevPlayBookData;
      });
    }
  }, [costData, sectionStarted.playbook]);

  // Helper function to recalculate OHP cost when OHP Rate changes
  const recalculateOHPCost = (newOhpRate, costData, updatedCostParams) => {
    if (!costData || !costData.breakdown || !costData._originalResult) {
      return costData;
    }

    const to4 = (n) => {
      if (n === null || n === undefined || n === '') return 0;
      if (typeof n === 'string') {
        const cleaned = n.replace(/[$,\s]/g, '').trim();
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
      }
      return Number(Number(n).toFixed(4));
    };

    // Get base material and process costs (these don't change)
    const material = Number(costData.breakdown.material || 0);
    const process = Number(costData.breakdown.process || 0);
    const baseOhpCost = Number(costData.meta?.baseOhpCost ?? costData.breakdown.ohp ?? 0);
    const baseOhpRate =
      Number(costData.meta?.baseOhpRate) ||
      (material + process > 0 ? (baseOhpCost / (material + process)) * 100 : 0);
    const baseFormulaCalc =
      material + process > 0 ? (material + process) * (baseOhpRate / 100) : 0;
    console.log(
      "🧮 Base OHP Formula:",
      `(${material} + ${process}) × (${baseOhpRate}/100) = ${baseFormulaCalc}`
    );

    // Recalculate OHP based on percentage change relative to original rate
    const targetOhpRate =
      typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate ?? 0);
    console.log(
      "➡️ Recalculating OHP:",
      `Current meta rate=${costData.meta?.currentOhpRate}, base rate=${baseOhpRate}`
    );

    let ohp = baseOhpCost;
    if (!isNaN(targetOhpRate)) {
      if (baseOhpRate > 0) {
        const rateRatio = targetOhpRate / baseOhpRate;
        const calc = baseOhpCost * rateRatio;
        console.log(
          "📐 OHP (based on ratio):",
          `(${baseOhpCost} * ${rateRatio}) = ${calc}`
        );
        ohp = to4(calc);
        } else {
        const ohpRateDecimal =
          targetOhpRate > 1 ? targetOhpRate / 100 : targetOhpRate;
        const calc = (material + process) * ohpRateDecimal;
        console.log(
          "📐 OHP (Material+Process * rate):",
          `(${material} + ${process}) * ${ohpRateDecimal} = ${calc}`
        );
        ohp = to4(calc);
      }
      const targetFormulaCalc =
        (material + process) * (targetOhpRate / 100);
      console.log(
        "🧮 Target OHP Formula:",
        `(${material} + ${process}) × (${targetOhpRate}/100) = ${targetFormulaCalc}`
      );
    }

    // Calculate new total: material + process + ohp
    const calculatedTotal = Number(material) + Number(process) + Number(ohp);
    const total = to4(calculatedTotal);

    // Update assumptions array with updated PlayBook values
    const resultData = costData._originalResult;
    const assumptionsArray = [];
    
    // Part Dimensions - from product data
    const dimensionData = productData?.partWeightDimensions || {};
    if (dimensionData.partLength && dimensionData.partWidth && dimensionData.finishWeight) {
      const heightPart = dimensionData.partHeight ? `, H: ${dimensionData.partHeight}mm` : "";
      assumptionsArray.push(
        `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm${heightPart}, Weight: ${dimensionData.finishWeight}g`
      );
    }
    
    // Machine Tonnage
    if (updatedCostParams.machineTonnage) {
      assumptionsArray.push(`Machine tonnage: ${updatedCostParams.machineTonnage}`);
    } else if (resultData.Tonnage || resultData.cTonnage) {
      assumptionsArray.push(`Machine tonnage: ${resultData.Tonnage || resultData.cTonnage}`);
    }
    
    // Cavities
    if (updatedCostParams.cavities) {
      assumptionsArray.push(`Cavities: ${updatedCostParams.cavities}`);
    } else if (resultData.cCavities || resultData.Cavities) {
      assumptionsArray.push(`Cavities: ${resultData.cCavities || resultData.Cavities}`);
    }
    
    // Cycle Time
    if (updatedCostParams.cycleTime) {
      assumptionsArray.push(`Cycle time: ${updatedCostParams.cycleTime}s`);
    } else if (resultData.CycleTime) {
      assumptionsArray.push(`Cycle time: ${resultData.CycleTime}s`);
    }
    
    // Machine Rate - always from stored procedure
    if (resultData.MHR) {
      const machineRate = typeof resultData.MHR === 'number' ? resultData.MHR : parseFloat(resultData.MHR);
      if (!isNaN(machineRate)) {
        assumptionsArray.push(`Machine rate: $${machineRate.toFixed(4)}/hour`);
      }
    }
    
    // Material Price - always from stored procedure
    if (resultData.RMPrice) {
      const materialPrice = typeof resultData.RMPrice === 'number' ? resultData.RMPrice : parseFloat(resultData.RMPrice);
      if (!isNaN(materialPrice)) {
        assumptionsArray.push(`Material price: $${materialPrice.toFixed(4)}/kg`);
      }
    }
    
    // Labour Cost - always from stored procedure
    if (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) {
      const labourCost = typeof (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) === 'number' 
        ? (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost)
        : parseFloat(resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost);
      if (!isNaN(labourCost)) {
        assumptionsArray.push(`Labour cost: $${labourCost.toFixed(4)}/unit`);
      }
    }
    
    // OHP Rate - use updated value
    if (newOhpRate !== null && newOhpRate !== undefined && newOhpRate !== 0) {
      const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
      if (!isNaN(ohpRateValue)) {
        assumptionsArray.push(`OHP rate: ${ohpRateValue.toFixed(2)}%`);
      }
    }

    // Update cost data with new OHP, total, and assumptions
    return {
      ...costData,
      breakdown: {
        ...costData.breakdown,
        ohp: ohp,
        total_should_cost: total,
      },
      summary: costData.summary.replace(
        /\$[\d.]+ per unit/,
        `$${total} per unit`
      ),
      assumptions:
        assumptionsArray.length > 0 ? assumptionsArray : costData.assumptions,
      meta: {
        ...costData.meta,
        currentOhpRate: targetOhpRate,
      },
    };
  };

  // Helper function to fetch material price and update material cost when tonnage changes
  const updateMaterialCostFromTonnage = async (tonnage, updatedCostParams) => {
    console.log("\n" + "=".repeat(80));
    console.log("💰 updateMaterialCostFromTonnage called");
    console.log("=".repeat(80));
    console.log("📥 Input Parameters:");
    console.log("   - Tonnage:", tonnage);
    console.log("   - CostData exists:", !!costData);
    console.log("   - CostData source:", costData?._source);
    console.log("   - ProductData exists:", !!productData);

    if (!costData || costData._source !== "stored_procedure" || !productData) {
      console.log("⚠️ Early return: Missing costData or productData");
      return;
    }

    try {
      const manufacturingCountry = productData.manufacturingCountry || "";
      const materialName = productData.materialSelection || null;
      const partWeightG = productData.partWeightDimensions?.finishWeight || 0;

      console.log("📋 Product Data:");
      console.log("   - Manufacturing Country:", manufacturingCountry);
      console.log("   - Material Name:", materialName);
      console.log("   - Part Weight (g):", partWeightG);

      if (!manufacturingCountry || !tonnage || partWeightG <= 0) {
        console.warn("⚠️ Cannot update material cost: missing country, tonnage, or part weight");
        console.log("   - Country:", manufacturingCountry || "MISSING");
        console.log("   - Tonnage:", tonnage || "MISSING");
        console.log("   - Part Weight:", partWeightG || "MISSING");
        return;
      }

      // Get current material cost before update
      const currentMaterialCost = costData.breakdown?.material || 0;
      const currentMaterialPricePerKg = costData.meta?.materialPricePerKg || null;
      
      console.log("\n📊 Current Values:");
      console.log("   - Current Material Cost:", `$${currentMaterialCost}/unit`);
      console.log("   - Current Material Price:", currentMaterialPricePerKg ? `$${currentMaterialPricePerKg}/kg` : "N/A");

      const requestPayload = {
        tonnage: tonnage,
        manufacturingCountry: manufacturingCountry,
        materialName: materialName,
      };

      console.log("\n📤 API Request Payload:");
      console.log(JSON.stringify(requestPayload, null, 2));
      console.log(`\n🔁 Fetching material price for ${tonnage} tons in ${manufacturingCountry}...`);

      const response = await apiFetch("/api/mech/material-price", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload),
      });

      console.log("📥 API Response Status:", response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Failed to fetch material price" }));
        console.error("❌ API Error Response:", errorData);
        throw new Error(errorData.error || "Failed to fetch material price");
      }

      const result = await response.json();
      console.log("\n📥 API Response Data:");
      console.log(JSON.stringify(result, null, 2));

      const newMaterialPricePerKg = parseFloat(result.material_price || 0);
      console.log("\n💰 Material Price Parsed:");
      console.log("   - Raw value:", result.material_price);
      console.log("   - Parsed value:", newMaterialPricePerKg);

      if (isNaN(newMaterialPricePerKg) || newMaterialPricePerKg <= 0) {
        console.warn("⚠️ Invalid material price received:", result);
        return;
      }

      // Calculate new material cost per unit: (part_weight_g / 1000) * material_price_per_kg
      // const partWeightKg = parseFloat(partWeightG) / 1000;
      // const newMaterialCost = Number((partWeightKg * newMaterialPricePerKg).toFixed(4));
      // Update Material in cost category table with material price per kg directly
      const newMaterialCost = Number(Number(newMaterialPricePerKg).toFixed(4));

      console.log("\n🧮 Calculation:");
      console.log("   - Material Price ($/kg):", newMaterialPricePerKg);
      console.log("   - Using material price per kg directly as material cost");
      console.log("   - New Material Cost ($/unit):", newMaterialCost);

      // Update costData with new material cost
      const to4 = (n) => {
        if (n === null || n === undefined || n === '') return 0;
        if (typeof n === 'string') {
          const cleaned = n.replace(/[$,\s]/g, '').trim();
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
        }
        return Number(Number(n).toFixed(4));
      };

      const process = to4(costData.breakdown.process || 0);
      const ohp = to4(costData.breakdown.ohp || 0);
      const newTotal = Number((newMaterialCost + process + ohp).toFixed(4));

      console.log("\n📊 Cost Breakdown Update:");
      console.log("   - Material (old):", `$${currentMaterialCost}/unit`);
      console.log("   - Material (new):", `$${newMaterialCost}/unit`);
      console.log("   - Process:", `$${process}/unit`);
      console.log("   - OHP:", `$${ohp}/unit`);
      console.log("   - Total (old):", `$${costData.breakdown.total_should_cost}/unit`);
      console.log("   - Total (new):", `$${newTotal}/unit`);
      console.log("   - Total Formula:", `${newMaterialCost} + ${process} + ${ohp} = ${newTotal}`);

      const updatedCostData = {
        ...costData,
        breakdown: {
          ...costData.breakdown,
          material: newMaterialCost,
          total_should_cost: newTotal,
        },
        summary: costData.summary.replace(
          /\$[\d.]+ per unit/,
          `$${newTotal} per unit`
        ),
        assumptions: costData.assumptions.map((assumption) => {
          // Update material price in assumptions if it exists
          if (assumption.includes("Material price:")) {
            return `Material price: $${newMaterialPricePerKg.toFixed(4)}/kg`;
          }
          return assumption;
        }),
        meta: {
          ...costData.meta,
          materialPricePerKg: newMaterialPricePerKg,
        },
      };

      setCostData(updatedCostData);
      console.log("\n✅ Material cost updated successfully!");
      console.log("   - New Material Price:", `$${newMaterialPricePerKg}/kg`);
      console.log("   - New Material Cost:", `$${newMaterialCost}/unit`);
      console.log("   - New Total Cost:", `$${newTotal}/unit`);
      console.log("=".repeat(80) + "\n");
    } catch (error) {
      console.error("\n❌ Failed to update material cost:");
      console.error("   - Error type:", error.constructor.name);
      console.error("   - Error message:", error.message);
      console.error("   - Stack:", error.stack);
      console.log("=".repeat(80) + "\n");
      // Don't show alert to user, just log the error
    }
  };

  const handlePlayBookChange = (field, value) => {
    // If updating costParams, merge it properly
    if (field === "costParams") {
      const previousOhpRate = playBookData?.costParams?.ohpRate;
      const newOhpRate = value.ohpRate;
      const previousTonnage = playBookData?.costParams?.machineTonnage;
      const newTonnage = value.machineTonnage;
      
      setPlayBookData({
        ...playBookData,
        costParams: value,
      });
      setKeyInsightsData(null);

      // If OHP Rate changed and we have existing costData, recalculate OHP cost
      if (costData && costData._source === "stored_procedure" && 
          newOhpRate !== undefined && newOhpRate !== null && 
          newOhpRate !== previousOhpRate) {
        const updatedCostData = recalculateOHPCost(newOhpRate, costData, value);
        setCostData(updatedCostData);
      }

      // For corrugated: Update OHP rate in assumptions when PlayBook changes
      if (costData && costData._source !== "stored_procedure" && 
          newOhpRate !== undefined && newOhpRate !== null && 
          newOhpRate !== previousOhpRate) {
        const updatedAssumptions = (costData.assumptions || []).map((assumption) => {
          // Replace "OHP: X%" or "OHP rate: X%" with the new value
          if (typeof assumption === 'string') {
            const lowerAssumption = assumption.toLowerCase();
            if (lowerAssumption.startsWith("ohp:") || lowerAssumption.startsWith("ohp rate:")) {
              const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
              if (!isNaN(ohpRateValue)) {
                return `OHP: ${ohpRateValue.toFixed(2)}%`;
              }
            }
          }
          return assumption;
        });
        
        // If OHP wasn't found in assumptions, add it
        const hasOhp = updatedAssumptions.some((a) => 
          typeof a === 'string' && (a.toLowerCase().startsWith("ohp:") || a.toLowerCase().startsWith("ohp rate:"))
        );
        if (!hasOhp && newOhpRate !== undefined && newOhpRate !== null && newOhpRate !== 0) {
          const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
          if (!isNaN(ohpRateValue)) {
            updatedAssumptions.push(`OHP: ${ohpRateValue.toFixed(2)}%`);
          }
        }

        setCostData({
          ...costData,
          assumptions: updatedAssumptions,
        });
      }

      // Note: Material price update is now handled when Quick Cost button is clicked
      // No automatic update here - wait for user to click Quick Cost button
    } else {
    setPlayBookData({ ...playBookData, [field]: value });
    }
  };

  // Helper function to analyze cost with explicit data
  const analyzeCostWithData = async (recipe, playbook, product) => {
    try {
      console.log("🚀 Analyzing cost, risk, and substitutes with:", { recipe, playbook, product });

      const payload = {
        recipe: recipe,
        playBookData: playbook,
        productData: product,
      };

      // Call all three APIs in parallel
      const [costResponse, riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/fnb/analyze-cost", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/analyze-risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/find-substitutes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      // Check responses
      if (!costResponse.ok) {
        const errorData = await costResponse
          .json()
          .catch(() => ({ error: "Cost analysis failed" }));
        throw new Error(errorData.error || "Failed to analyze cost");
      }

      // Parse all responses (non-blocking for risk and substitute)
      const costResult = await costResponse.json();
      console.log("✅ Cost analysis complete:", costResult);
      setCostData(costResult);

      // Handle risk analysis
      if (riskResponse.ok) {
        const riskResult = await riskResponse.json();
        console.log("✅ Risk analysis complete:", riskResult);
        setRiskData(riskResult);
      } else {
        console.warn("⚠️ Risk analysis failed, continuing without risk data");
        setRiskData(null);
      }

      // Handle substitute analysis
      if (substituteResponse.ok) {
        const substituteResult = await substituteResponse.json();
        console.log("✅ Substitute analysis complete:", substituteResult);
        setSubstituteData(substituteResult);
      } else {
        console.warn("⚠️ Substitute analysis failed, continuing without substitute data");
        setSubstituteData(null);
      }

      // Generate product image with the playbook data
      await generateProductImageWithData(product, playbook);

      // Navigate to results section and mark as started
      setActiveSection("results");
      setSectionStarted((prev) => ({ ...prev, results: true }));

      return costResult;
    } catch (error) {
      console.error("Analysis failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      throw error;
    }
  };

  const handleAnalyzeCost = async () => {
    if (!recipeData || !playBookData || !productData) return;

    setLoadingMessage("Running cost analysis...");
    setIsProcessing(true);
    try {
      await analyzeCostWithData(recipeData, playBookData, productData);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  // Fetch risk and substitute analysis for Analysis Report tabs.
  const fetchRiskAndSubstituteData = async (product, playbook, costBreakdownOverride = null) => {
    if (!product) {
      setRiskData(null);
      setSubstituteData(null);
      return;
    }

    const payload = {
      towerName: "Packaging",
      costModel: costBreakdownOverride || costData?.breakdown || {},
      form: {
        productData: product || {},
        playBookData: playbook || {},
        recipe: recipeData || [],
      },
    };

    const fallbackRecipe = Array.isArray(recipeData) && recipeData.length > 0
      ? recipeData
      : [{ ingredient: product?.materialSelection || "Glass Bottle", percentage: 100, functional_group: "Base" }];

    const fnbPayload = {
      recipe: fallbackRecipe,
      playBookData: playbook || {},
      productData: product || {},
    };

    const buildFallbackRisk = () => ({
      summary: `Risk snapshot for ${product?.productName || "glass bottle packaging"} based on available costing inputs.`,
      top_concerns: [
        "Raw material and energy cost variability can shift unit economics.",
        "Cycle-time and reject-rate variability can raise conversion cost.",
        "Supplier lead-time fluctuations can impact supply continuity.",
      ],
      risk_items: [
        { title: "Material and Utility Volatility", severity: 6, probability: 6, detail: "Track cullet mix, energy tariffs, and supplier pricing cadence." },
        { title: "Yield and Process Variability", severity: 5, probability: 5, detail: "Monitor cycle adherence, breakage, and defect-related scrap." },
      ],
      assumptions: "Generated from glass bottle product and playbook inputs (fallback mode).",
    });

    const buildFallbackSubstitute = () => {
      const details = product?.partDetails || {};
      const material = product?.materialSelection || "Current glass specification";
      const volume = details.volume || product?.partWeightDimensions?.finishWeight || "Current volume";
      const finish = details.neckFinish || "Current neck finish";
      const decoration = details.decoration || "Current decoration setup";

      return {
        summary: `Glass bottle optimization recommendations generated for ${product?.productName || "the current packaging"} using provided inputs.`,
        substitutes: [
          {
            current_item: `Material/spec: ${material}, Volume: ${volume}`,
            proposed_item: "Validate lightweight design with equivalent performance",
            estimated_cost_delta_per_unit: "Potential material and logistics cost reduction",
            impact_on_quality: "Requires top-load and handling validation",
            feasibility_score: 6,
            timeline_weeks: 4,
            risk_note: "Confirm mechanical performance and filling-line compatibility.",
            validation_steps: ["Design review", "Top-load testing", "Line trial"],
          },
          {
            current_item: `Finish/decoration: ${finish}, ${decoration}`,
            proposed_item: "Optimize finish and decoration process parameters",
            estimated_cost_delta_per_unit: "Potential conversion and consumables cost reduction",
            impact_on_quality: "Low to medium",
            feasibility_score: 7,
            timeline_weeks: 3,
            risk_note: "Verify sealing, aesthetics, and brand requirements.",
            validation_steps: ["Process trial", "Seal integrity check", "Visual QA"],
          },
        ],
        prioritization: [
          "Process/decoration optimization for near-term savings",
          "Lightweight design optimization after validation",
        ],
        assumptions: "Generated from glass bottle product and playbook inputs (fallback mode).",
      };
    };

    try {
      let [riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/ai/costing-v4/risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/ai/costing-v4/substitute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      if (!riskResponse.ok || !substituteResponse.ok) {
        [riskResponse, substituteResponse] = await Promise.all([
          apiFetch("/api/fnb/analyze-risk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
          apiFetch("/api/fnb/find-substitutes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
        ]);
      }

      if (riskResponse.ok) {
        setRiskData(await riskResponse.json());
      } else {
        setRiskData(buildFallbackRisk());
      }

      if (substituteResponse.ok) {
        setSubstituteData(await substituteResponse.json());
      } else {
        setSubstituteData(buildFallbackSubstitute());
      }
    } catch (error) {
      console.error("Risk/Substitute analysis failed:", error);
      setRiskData(buildFallbackRisk());
      setSubstituteData(buildFallbackSubstitute());
    }
  };

  const generateProductImageWithData = async (product, playbook) => {
    if (!product) return;

    setGeneratingImage(true);

    try {
      const imagePayload = {
        productName: product.productName,
        companyName: "Kearney",
        brandName: "PERLab",
        commodity: "Glass Bottles",
        primaryPackaging: playbook?.primary_packaging || "",
        skuWt: product.skuWeight || "",
        skuWtUnit: product.skuWeightUnit || "g",
        marketPositioning: product.marketPositioning || "",
        style: "professional",
      };

      console.log("🖼️ Generating product image:", imagePayload);

      const response = await apiFetch("/api/pkg/glassbottles/generate-product-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate image");
      }

      const data = await response.json();
      setProductImageUrl(data.image_url);
      console.log("✅ Product image generated:", data.image_url);
    } catch (error) {
      console.error("❌ Error generating product image:", error);
      // Don't alert - image is optional
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateProductImage = () => {
    return generateProductImageWithData(productData, playBookData);
  };

  const fetchKeyInsights = async (product, playbook) => {
    if (!product) {
      return;
    }
    try {
      const response = await apiFetch("/api/mech/key-insights", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productData: product,
          playBookData: playbook || {},
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to generate key insights");
      }
      const insights = await response.json();
      setKeyInsightsData(insights);
    } catch (error) {
      console.error("Failed to fetch key insights:", error);
    }
  };

  // Call corrugated costing calculation
  const callCorrugatedCosting = async (productData, playBookData) => {
    try {
      console.log("📞 Calling corrugated costing API with:", { productData, playBookData });

      // Prepare payload - convert dimensions from mm to cm
      const dimensionData = productData.partWeightDimensions || {};
      const detailsData = productData.partDetails || {};
      
      // Convert dimensions from mm to cm for the calculation
      const lengthCm = dimensionData.partLength ? parseFloat(dimensionData.partLength) / 10 : 0;
      const widthCm = dimensionData.partWidth ? parseFloat(dimensionData.partWidth) / 10 : 0;
      const heightCm = dimensionData.partHeight ? parseFloat(dimensionData.partHeight) / 10 : 0;

      const payload = {
        productData: {
          ...productData,
          partWeightDimensions: {
            ...dimensionData,
            length_cm: lengthCm,
            width_cm: widthCm,
            height_cm: heightCm,
          },
          partDetails: detailsData,
        },
        playBookData: playBookData || {},
      };

      console.log("📤 Payload being sent:", payload);

      // Call the corrugated costing API endpoint
      const response = await apiFetch("/api/costing/corrugated", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // Read response as text first to handle both success and error cases
      const responseText = await response.text();
      
      if (!response.ok) {
        // Try to parse error response as JSON
        let errorMessage = `Corrugated costing call failed (${response.status})`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
        } catch (jsonErr) {
          // If JSON parsing fails, use the text as error message
          errorMessage = responseText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Parse successful response as JSON
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (jsonErr) {
        console.error("Failed to parse response as JSON:", jsonErr);
        console.error("Response text:", responseText);
        throw new Error("Invalid JSON response from server");
      }
      
      console.log("✅ Corrugated costing result:", result);
      return result;
    } catch (err) {
      console.error("❌ Corrugated costing call failed:", err);
      throw err;
    }
  };

  // New Laminates Quick Cost handler
  const callLaminatesCosting = async (productData) => {
    try {
      console.log("📞 Calling laminates costing API with:", { productData });

      const payload = {
        productData: productData || {},
      };

      const response = await apiFetch("/api/costing/laminates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();

      if (!response.ok) {
        let errorMessage = `Laminates costing call failed (${response.status})`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
          if (errorData.error_message) {
            errorMessage += ` | ${errorData.error_message}`;
          }
        } catch (jsonErr) {
          errorMessage = responseText || errorMessage;
        }
        console.error("Laminates costing error body:", responseText);
        throw new Error(errorMessage);
      }

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (jsonErr) {
        console.error("Failed to parse laminates response as JSON:", jsonErr);
        console.error("Response text:", responseText);
        throw new Error("Invalid JSON response from laminates costing API");
      }

      console.log("✅ Laminates costing result:", result);
      return result;
    } catch (err) {
      console.error("❌ Laminates costing call failed:", err);
      throw err;
    }
  };

  // Call glassbottle costing calculation with SSE streaming
  const callGlassBottleCostingStream = async (productData, playBookData) => {
    try {
      const timestamp = Date.now();
      
      // Prepare request payload matching backend expectations
      const requestData = {
        productData: productData,  // Send full productData object
        projectId: `PROJ_${timestamp}`,
        partKey: `P1`,
        // Include playbook if available
        ...(playBookData && { existingPlaybook: playBookData }),
      };

      console.log("📞 Calling glassbottle streaming endpoint:", requestData);

      // Call SSE streaming endpoint
      const response = await apiFetch("/api/packaging/glassbottle/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      console.log("📥 Response received, status:", response.status);

      console.log("📥 Response status:", response.status, response.statusText);
      console.log("📥 Response headers:", Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Response error:", errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Check if response is actually SSE
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("text/event-stream")) {
        console.warn("⚠️ Response is not SSE, content-type:", contentType);
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let completeEvent = null;
      let errorEvent = null;
      const allEvents = []; // Track all events for debugging

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Process any remaining data in buffer
          if (buffer.trim()) {
            const events = buffer.split("\n\n");
            for (const event of events) {
              if (!event.trim()) continue;
              const dataMatch = event.match(/^data: (.+)$/m);
              if (dataMatch) {
                try {
                  const eventData = JSON.parse(dataMatch[1]);
                  console.log("📥 Final SSE Event:", eventData);
                  allEvents.push(eventData);
                  if (eventData.type === "complete") {
                    completeEvent = eventData;
                  } else if (eventData.type === "error") {
                    errorEvent = eventData;
                  }
                } catch (parseErr) {
                  console.warn("Failed to parse final SSE event:", parseErr, "Raw:", dataMatch[1]);
                }
              } else {
                console.warn("⚠️ Final buffer event doesn't match SSE format:", event.substring(0, 100));
              }
            }
          }
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete events (separated by \n\n)
        const events = buffer.split("\n\n");
        buffer = events.pop() || ""; // Keep incomplete event in buffer

        for (const event of events) {
          if (!event.trim()) continue;

          // Extract JSON from "data: {...}" format
          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) {
            console.warn("⚠️ Event doesn't match SSE format:", event.substring(0, 100));
            continue;
          }

          try {
            const eventData = JSON.parse(dataMatch[1]);
            console.log("📥 SSE Event:", eventData);
            allEvents.push(eventData);

            if (eventData.type === "complete") {
              // Check if it's an error complete event
              if (eventData.status === "error") {
                console.error("❌ Complete event with error status:", eventData);
                // Build detailed error message
                let errorMessage = eventData.message || "Analysis failed";
                if (eventData.failed_group) {
                  errorMessage += ` (Failed in group ${eventData.failed_group})`;
                }
                if (eventData.error) {
                  errorMessage += `: ${eventData.error}`;
                }
                throw new Error(errorMessage);
              }
              // Analysis complete - return the final response
              console.log("✅ Analysis complete:", eventData);
              return eventData;
            } else if (eventData.type === "error") {
              console.error("❌ Error event received:", eventData);
              errorEvent = eventData;
              // Build detailed error message
              let errorMessage = eventData.message || "Analysis failed";
              if (eventData.error) {
                errorMessage += `: ${eventData.error}`;
              }
              throw new Error(errorMessage);
            } else if (eventData.type === "progress") {
              // Log progress events, including errors
              if (eventData.status === "error") {
                console.error(`❌ Progress event with error:`, eventData.label || eventData.schema_type, eventData.error || "");
              } else {
                console.log(`📊 ${eventData.type} event:`, eventData.label || eventData.message || "");
              }
            } else if (eventData.type === "start") {
              console.log(`📊 ${eventData.type} event:`, eventData.label || eventData.message || "");
            }
          } catch (parseErr) {
            if (parseErr instanceof Error && parseErr.message !== "Analysis failed") {
              // Re-throw if it's our error
              throw parseErr;
            }
            console.warn("Failed to parse SSE event:", parseErr, "Raw:", dataMatch[1]);
          }
        }
      }

      // Log all events received for debugging
      console.log("📋 All events received:", allEvents);

      // If we have a complete event from the final buffer, return it
      if (completeEvent) {
        // Check if it's an error complete event
        if (completeEvent.status === "error") {
          console.error("❌ Complete event with error status:", completeEvent);
          // Build detailed error message
          let errorMessage = completeEvent.message || "Analysis failed";
          if (completeEvent.failed_group) {
            errorMessage += ` (Failed in group ${completeEvent.failed_group})`;
          }
          if (completeEvent.error) {
            errorMessage += `: ${completeEvent.error}`;
          }
          throw new Error(errorMessage);
        }
        console.log("✅ Analysis complete (from final buffer):", completeEvent);
        return completeEvent;
      }

      // If we have an error event, throw it
      if (errorEvent) {
        // Build detailed error message
        let errorMessage = errorEvent.message || "Analysis failed";
        if (errorEvent.error) {
          errorMessage += `: ${errorEvent.error}`;
        }
        throw new Error(errorMessage);
      }

      // If no complete or error event, provide detailed error
      console.error("❌ Stream ended without complete event. Events received:", allEvents);
      throw new Error(`Stream ended without complete event. Received ${allEvents.length} events: ${allEvents.map(e => e.type).join(", ")}`);
    } catch (err) {
      console.error("❌ Glassbottle streaming call failed:", err);
      throw err;
    }
  };

  const runQuickCost = async () => {
    if (!productData) return;

    setLoadingMessage("Generating Quick Cost...");
    setIsProcessing(true);
    setKeyInsightsData(null);
    setRiskData(null);
    setSubstituteData(null);

    try {
      // Call glassbottle costing calculation with streaming
      console.log("📞 Calling glassbottle costing with streaming:", { productData, playBookData });

      const costResult = await callGlassBottleCostingStream(productData, playBookData);

      // Handle the response from streaming endpoint
      if (costResult && costResult.status === 'success') {
        console.log("✅ Glassbottle costing completed:", costResult);
        
        // Extract should cost result
        const shouldCost = costResult.ShouldCostResult || {};
        const material = parseFloat(shouldCost.MaterialCost || 0);
        const process = parseFloat(shouldCost.ProcessCost || 0);
        const ohp = parseFloat(shouldCost.OHPCost || 0);
        const total = parseFloat(shouldCost.TotalCost || 0);

        // Extract cycle_time_sec from costResult (from backend response)
        let cycleTimeSeconds = 0;
        if (costResult.cycleTime_sec !== undefined && costResult.cycleTime_sec !== null) {
          cycleTimeSeconds = parseFloat(costResult.cycleTime_sec) || 0;
        } else if (costResult.AssumptionsJson?.benchmarked_assumptions?.process_benchmarks?.primary_forming?.cycle_time_sec !== undefined) {
          cycleTimeSeconds = parseFloat(costResult.AssumptionsJson.benchmarked_assumptions.process_benchmarks.primary_forming.cycle_time_sec) || 0;
        }

        // Build assumptions array from response
        let assumptionsArray = [];
        if (costResult.AssumptionsJson) {
          // Extract assumptions from AssumptionsJson if available
          const assumptions = costResult.AssumptionsJson;
          if (Array.isArray(assumptions)) {
            assumptionsArray = assumptions;
          } else if (typeof assumptions === 'object') {
            // Flatten assumptions object into array
            Object.values(assumptions).forEach((section) => {
              if (Array.isArray(section)) {
                assumptionsArray.push(...section);
              }
            });
          }
        }

        // Build assumptions from product data if not available
        if (assumptionsArray.length === 0 && productData) {
          const dimensionData = productData?.partWeightDimensions || {};
          if (dimensionData.finishWeight) {
            assumptionsArray.push(`Part weight: ${dimensionData.finishWeight}g`);
          }
          if (dimensionData.partThickness) {
            assumptionsArray.push(`Thickness: ${dimensionData.partThickness}mm`);
          }
          if (productData.batchSize) {
            assumptionsArray.push(`Batch size: ${productData.batchSize}`);
          }
          if (productData.annualVolume) {
            assumptionsArray.push(`Annual volume: ${productData.annualVolume}`);
          }
          if (productData.manufacturingCountry) {
            assumptionsArray.push(`Manufacturing country: ${productData.manufacturingCountry}`);
          }
        }

        // Transform to format expected by ResultsSection
        const transformedCostData = {
          summary: `Should cost analysis for ${productData.productName || "glass bottle"} manufactured in ${productData.manufacturingCountry || "location"}. Total cost: $${total.toFixed(4)} per unit.`,
          breakdown: {
            material: material,
            process: process,
            packaging: 0,
            logistics: 0,
            ohp: ohp,
            total_should_cost: total,
            currency: "USD/unit",
          },
          line_items: [],
          key_insights: [],
          assumptions: assumptionsArray,
          _source: "glassbottle_routes",
          _originalResult: costResult,
          formulaResult: costResult.formulaResult, // Store formula result for assumptions mapping
          cycleTime_sec: cycleTimeSeconds, // Store cycle time for PlayBook initialization
        };

        setCostData(transformedCostData);

        // Calculate OHP rate from costData (matching the percentage in cost category table)
        const calculatedOhpRate = calculateOhpRateFromCostData(transformedCostData);

        // Initialize playBookData with OHP rate and cycle time if available
        setPlayBookData((prevPlayBookData) => {
          const currentCostParams = prevPlayBookData?.costParams || {};
          const updates = { ...currentCostParams };
          
          // Only update ohpRate if not already set (preserve user input)
          if (calculatedOhpRate > 0 && (currentCostParams.ohpRate === undefined || currentCostParams.ohpRate === 0 || currentCostParams.ohpRate === null)) {
            updates.ohpRate = calculatedOhpRate;
          }
          
          // Only update cycleTime if not already set (preserve user input)
          if (cycleTimeSeconds > 0 && (currentCostParams.cycleTime === undefined || currentCostParams.cycleTime === 0 || currentCostParams.cycleTime === null)) {
            updates.cycleTime = cycleTimeSeconds;
          }
          
          // Only update if there are changes
          if (Object.keys(updates).length > Object.keys(currentCostParams).length || 
              updates.ohpRate !== currentCostParams.ohpRate || 
              updates.cycleTime !== currentCostParams.cycleTime) {
            return {
              ...prevPlayBookData,
              costParams: updates,
            };
          }
          return prevPlayBookData;
        });

        if (costResult.riskData) {
          const unwrappedRiskData = costResult.riskData.riskData || costResult.riskData;
          setRiskData(unwrappedRiskData);
        }

        if (costResult.substituteData) {
          const unwrappedSubstituteData = costResult.substituteData.substituteData || costResult.substituteData;
          setSubstituteData(unwrappedSubstituteData);
        }

        if (!costResult.riskData || !costResult.substituteData) {
          await fetchRiskAndSubstituteData(
            productData,
            playBookData || {},
            {
              material: costResult?.ShouldCostResult?.MaterialCost || 0,
              process: costResult?.ShouldCostResult?.ProcessCost || 0,
              ohp: costResult?.ShouldCostResult?.OHPCost || 0,
            }
          );
        }

        // Navigate to results section
        setActiveSection("results");
        setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));

        // Fetch key insights
        await fetchKeyInsights(productData, playBookData || {});
        // Trigger image generation after Quick Cost completes (non-blocking)
        generateProductImageWithData(productData, playBookData || {});
      } else {
        throw new Error(costResult.message || "Glassbottle costing failed");
      }
    } catch (error) {
      console.error("Quick Cost failed:", error);
      alert(`Failed to calculate cost: ${error.message}`);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please fill in product information first");
      return;
    }

    console.log("Starting Progressive Quick Cost Analysis for Glass Bottles...");

    // Initialize progress steps immediately with descriptive labels
    const initialSteps = [
      { id: "step-1", label: "Documenting Assumptions", status: "in-progress", message: "Processing..." },
      { id: "step-2", label: "Analyzing Material Requirements", status: "pending", message: "" },
      { id: "step-3", label: "Calculating Process Costs", status: "pending", message: "" },
      { id: "step-4", label: "Computing Machine Parameters", status: "pending", message: "" },
      { id: "step-5", label: "Generating Cost Breakdown", status: "pending", message: "" },
      { id: "step-6", label: "Finalizing Analysis Report", status: "pending", message: "" },
    ];
    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    setShowProgressTracker(true);
    setKeyInsightsData(null);
    setRiskData(null);
    setSubstituteData(null);

    try {
      // Step 1: Documenting Assumptions (16.67%)
      setProgressSteps((prev) =>
        prev.map((step, idx) =>
          idx === 0 ? { ...step, status: "in-progress", message: "Processing..." } : step
        )
      );
      setCurrentProgressStep(1);
      setOverallProgress(16.67);

      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate work

      // Step 2: Analyzing Material Requirements (33.33%)
      setProgressSteps((prev) =>
        prev.map((step, idx) =>
          idx === 0 ? { ...step, status: "completed", message: "Complete" } :
          idx === 1 ? { ...step, status: "in-progress", message: "Processing..." } : step
        )
      );
      setCurrentProgressStep(2);
      setOverallProgress(33.33);

      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate work

      // Step 3: Calculating Process Costs (50%) - Call analysis here
      setProgressSteps((prev) =>
        prev.map((step, idx) =>
          idx === 1 ? { ...step, status: "completed", message: "Complete" } :
          idx === 2 ? { ...step, status: "in-progress", message: "Processing..." } : step
        )
      );
      setCurrentProgressStep(3);
      setOverallProgress(50);

      // Run analysis
      console.log("Running Quick Cost analysis with:", productData);
      await runQuickCost();

      // Step 4: Computing Machine Parameters (66.67%)
      setProgressSteps((prev) =>
        prev.map((step, idx) =>
          idx === 2 ? { ...step, status: "completed", message: "Complete" } :
          idx === 3 ? { ...step, status: "in-progress", message: "Processing..." } : step
        )
      );
      setCurrentProgressStep(4);
      setOverallProgress(66.67);

      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate work

      // Step 5: Generating Cost Breakdown (83.33%)
      setProgressSteps((prev) =>
        prev.map((step, idx) =>
          idx === 3 ? { ...step, status: "completed", message: "Complete" } :
          idx === 4 ? { ...step, status: "in-progress", message: "Processing..." } : step
        )
      );
      setCurrentProgressStep(5);
      setOverallProgress(83.33);

      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate work

      // Step 6: Finalizing Analysis Report (100%)
      setProgressSteps((prev) =>
        prev.map((step, idx) =>
          idx === 4 ? { ...step, status: "completed", message: "Complete" } :
          idx === 5 ? { ...step, status: "in-progress", message: "Processing..." } : step
        )
      );
      setCurrentProgressStep(6);
      setOverallProgress(100);

      // Mark all complete
      setProgressSteps((prev) => prev.map((step) => ({ ...step, status: "completed", message: "Complete" })));

      // Close dialog
      setShowProgressTracker(false);
      setIsProcessing(false);

      console.log("Progressive Quick Cost completed successfully!");
    } catch (error) {
      console.error("Progressive Quick Cost failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      setIsProcessing(false);
      setShowProgressTracker(false);
    }
  };

  const handleQuickCost = async () => {
    // Use progressive tracker for better UX
    return handleProgressiveQuickCost();
  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description:
        "Answer a few quick questions about your product to generate an AI-driven cost analysis with  manufacturing parameters, and detailed insights.",
    },
    // {
    //   id: "formulation",
    //   label: "Formulation",
    //   icon: <ScienceIcon />,
    //   disabled: false,
    //   title: "Ready to Build Formulation",
    //   description: "View and edit ingredient composition with functional groups and percentages.",
    // },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);
 
  const costTotal = useMemo(() => {
    const declaredTotal =
      costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    console.log(costRows)
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Packaging"
          industry="Packaging"
          commodity="Glass Bottles"
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {/* Show info notification only for product section when not started */}
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {/* Keep CookiesQuestions mounted but hidden when not active to preserve state */}
            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none", height:"92%"}}>
                <GlassbottlesQuestions
                  onComplete={handleProductComplete}
                  onDataChange={setProductData}
                  initialData={productData}
                />
              </Box>
            )}

            {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                <FormulationTable
                  data={recipeData}
                  onRowChange={(index, field, value) => {
                    const updated = [...recipeData];
                    updated[index][field] = value;
                    setRecipeData(updated);
                  }}
                  onRowAdd={() => {
                    setRecipeData([
                      ...recipeData,
                      { ingredient: "", percentage: 0, functional_group: null },
                    ]);
                  }}
                  onRowDelete={(index) => {
                    setRecipeData(recipeData.filter((_, i) => i !== index));
                  }}
                  brandColor="#ff6034"
                  editable={true}
                  showTotal={true}
                />
              </Box>
            )}

            {/* Show info notification for playbook section when not started */}
            {!sectionStarted.playbook && activeSection === "playbook" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <GlassbottlesPlayBook data={playBookData} onChange={handlePlayBookChange} />
                ) : (
                  <GlassbottlesPlayBook data={{}} onChange={handlePlayBookChange} />
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {costData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    substituteSectionTitle="Glass Bottle Recommendations"
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    onRegenerateImage={generateProductImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor="#ff6034"
                    assumptions={costData?.assumptions || []}
                    keyInsights={keyInsightsData}
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions={renderAssumptions({
                      assumptions: costData?.assumptions || [],
                      productData: productData,
                      formulaResult: costData?.formulaResult,
                      perlColors: perlColors,
                      perlTypography: perlTypography,
                    })}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Quick Cost Button */}
        {sectionStarted.product &&  (
          <QuickCostButton
            onClick={handleQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor="#ff6034"
          />
        )}

        {/* Progressive Analysis Tracker Dialog */}
        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
